/**
 * How many rows the current scope really matches.
 *
 * Medplum's GraphQL returns no total, so "Showing the first 2,000 of 6,431" has
 * to get its denominator from a separate REST count. `_count=0` makes that a
 * count with no rows attached, which is cheap enough to re-run whenever the
 * scope changes.
 *
 * Two callers: the truncation notice, and `useQueuePage`'s "Showing n of N". A
 * tab that loaded its whole cohort already knows the total — it is the number
 * of rows it is holding.
 */

import { useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';

export function useCohortTotal(
  resourceType: 'Patient' | 'Appointment',
  filter: string | undefined
): number | undefined {
  const medplum = useMedplum();
  const [total, setTotal] = useState<number | undefined>();

  useEffect(() => {
    if (!filter) {
      setTotal(undefined);
      return;
    }

    let cancelled = false;

    async function count(): Promise<void> {
      const bundle = await medplum.search(resourceType, {
        _filter: filter as string,
        _count: '0',
        _total: 'accurate',
      });

      if (!cancelled) {
        setTotal(bundle.total);
      }
    }

    count().catch(() => {
      if (!cancelled) {
        // Decoration on a queue that already works — see the header comment.
        setTotal(undefined);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [medplum, resourceType, filter]);

  return total;
}
